import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { defer } from 'es-toolkit/compat';
import { Capacitor } from '@capacitor/core';
import { usePostHog } from 'posthog-js/react';
import { ENV } from 'shared/ENV';
import { formatToUSDollars, generateClientToken } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';
import { useDonationState } from 'features/payments/hooks/useDonationState';
import { API } from 'api';
import { User } from 'shared/types';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { useQueryParams } from 'hooks/useQueryParams';
import { CheckoutPriceSummary } from 'features/payments/components/CheckoutPriceSummary';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Loader } from 'components/Loader';
import { TRACKING_ID_PARAM } from '../../../../constants';
import { MembershipSuggestion } from './MembershipSuggestion';

type DonationType = 'one-time' | 'monthly';

type FormValues = {
  donationType: DonationType;
  donationAmount: number;
  customDonationAmount: number;
};

type QueryParams = {
  frequency?: string;
  amount?: string;
};

const ONE_DONATION_AMOUNTS = [10, 25, 50, 100];
const MONTHLY_DONATION_AMOUNTS = [5, 10, 20, 40];

const DEFAULT_DONATION_AMOUNT: Record<DonationType, number> = {
  'one-time': 25,
  monthly: 10,
};

const getFormValuesFromParams = (params: QueryParams): Partial<FormValues> => {
  const { frequency: paramFrequency, amount: paramAmount } = params;

  if (!paramFrequency || !paramAmount) return {};

  const frequency: DonationType =
    paramFrequency === 'onetime' ? 'one-time' : 'monthly';
  const amount = Number(paramAmount);

  const isPresetAmount =
    frequency === 'one-time'
      ? ONE_DONATION_AMOUNTS.includes(amount)
      : MONTHLY_DONATION_AMOUNTS.includes(amount);

  if (frequency === 'one-time') {
    return {
      donationType: frequency,
      donationAmount: isPresetAmount ? amount : 0,
      customDonationAmount: isPresetAmount ? 0 : amount,
    };
  }

  return {
    donationType: frequency,
    donationAmount: isPresetAmount ? amount : DEFAULT_DONATION_AMOUNT.monthly,
    customDonationAmount: 0,
  };
};

const saveVerificationToken = async (
  userId: number,
  verificationToken: string,
): Promise<void> => {
  await API.post('recurly_integration/one_time_donation_token/', {
    userId,
    verificationToken,
  });
};

const getDonateLink = (
  donationAmount: string,
  donationRenews: string,
  token: string,
  isSupporter: boolean,
  user: User | null,
  continueOnWeb: boolean,
  posthogId?: string,
): string => {
  const params = new URLSearchParams({ donationAmount, donationRenews });
  if (isSupporter && user) {
    params.append('email', user.email);
    if (user.firstName) {
      params.append('firstName', user.firstName);
    }
    if (user.lastName) {
      params.append('lastName', user.lastName);
    }
    params.append('userId', user.id.toString());
    params.append('verificationToken', token);
  } else {
    params.append('clientToken', token);

    // if this is a legacy user with no membership or prior donation, we should still pass along their info
    if (user) {
      // Do not use encodeURIComponent - URLSearchParams encodes values on serialization;
      // pre-encoding causes double-encoding.
      params.append('email', user.email);
    }
    if (user?.firstName) {
      params.append('firstName', user.firstName);
    }
    if (user?.lastName) {
      params.append('lastName', user.lastName);
    }
  }
  // Add PostHog ID for cross-platform tracking
  if (posthogId && continueOnWeb) {
    params.append(TRACKING_ID_PARAM, posthogId);
  }
  if (continueOnWeb) {
    return `${ENV.VITE_BASE_WEB_URL}donate/account_info?${params.toString()}`;
  }
  return '/donate/account_info';
};

export const DonateContent = (): JSX.Element => {
  const { t } = useTranslation();
  const [verificationError, setVerificationError] = useState(false);
  const { user, isSupporter, hasActiveMembership } = useAuthState();
  const { updateDonationState } = useDonationState();
  const history = useHistory();
  const posthog = usePostHog();
  const [queryParams] = useQueryParams<QueryParams>();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        donationType: Yup.mixed<DonationType>(),
        donationAmount: Yup.number(),
        customDonationAmount: Yup.number().test(
          'minOrZero',
          t('donate.inputs.customAmount.invalid'),
          (value) => !value || value >= 10,
        ),
      }),
    [t],
  );

  const { control, setValue, watch, getValues, trigger } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      donationType: 'one-time',
      donationAmount: DEFAULT_DONATION_AMOUNT['one-time'],
      customDonationAmount: 0,
      ...getFormValuesFromParams(queryParams),
    },
    mode: 'onBlur',
  });

  const [donationType, donationAmount, customDonationAmount] = watch([
    'donationType',
    'donationAmount',
    'customDonationAmount',
  ]);

  // this is used as either a verificationToken or a clientToken depending if they are already a supporter
  const tokenRef = useRef<string | null>(null);
  if (!tokenRef.current) {
    tokenRef.current = generateClientToken();
  }

  const { mutate: saveDonationToken, isPending } = useMutation({
    mutationFn: async (userId: number) => {
      setVerificationError(false);
      await saveVerificationToken(userId, tokenRef.current as string);
    },
    onError: (error) => {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
      setVerificationError(true);
    },
  });

  const userId = user?.id;

  useEffect(() => {
    if (isSupporter && userId) {
      saveDonationToken(userId);
    }
  }, [saveDonationToken, userId, isSupporter]);

  const handleDonationTypeChange = (newValue: string | null): void => {
    if (newValue === null) return;
    const newDonType = newValue as DonationType;
    setValue('donationType', newDonType);
    if (newDonType !== donationType) {
      setValue('donationAmount', DEFAULT_DONATION_AMOUNT[newDonType]);
    }
    if (newDonType === 'monthly' && customDonationAmount) {
      setValue('customDonationAmount', 0);
    }
  };

  const handleDonationAmountChange = (newValue: string | null): void => {
    if (newValue === null) return;
    setValue('donationAmount', parseInt(newValue as string, 10));
    if (customDonationAmount) {
      setValue('customDonationAmount', 0);
    }
  };

  const handleCustomDonationAmountChange = (newValue: string): void => {
    if (newValue.split('.')[1]?.length > 2) {
      // Allow only 2 decimal places
      return;
    }
    const amount = Number(newValue);
    if (!Number.isNaN(amount)) {
      setValue('customDonationAmount', amount);
    }
    if (amount === 0) {
      setValue('donationAmount', DEFAULT_DONATION_AMOUNT['one-time']);
    } else if (donationAmount) {
      setValue('donationAmount', 0);
    }
  };

  const handleNext = async (): Promise<void> => {
    const formValues = getValues();
    const isValid = await trigger();

    if (!isValid) return;

    const continueOnWeb = Capacitor.isNativePlatform();

    // Get PostHog distinct ID for native app users
    const posthogId = continueOnWeb ? posthog.get_distinct_id() : undefined;

    const donateLink = getDonateLink(
      (formValues.customDonationAmount || formValues.donationAmount).toString(),
      formValues.donationType === 'monthly' ? 'true' : 'false',
      tokenRef.current as string,
      isSupporter,
      user,
      continueOnWeb,
      posthogId,
    );

    if (continueOnWeb) {
      window.open(donateLink, '_blank');
      // Go back to main page - use case: user goes back to wd app
      // Defer action to prevent user from noticing the page change before
      // being redirected to the donate account information page
      defer(() => history.push('/'));
      return;
    }

    if (isSupporter && user) {
      updateDonationState({
        donationAmount:
          formValues.customDonationAmount || formValues.donationAmount,
        donationRenews: formValues.donationType === 'monthly',
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        existingUser: true,
        verificationToken: tokenRef.current as string,
        userId: user.id,
      });
    } else {
      updateDonationState({
        donationAmount:
          formValues.customDonationAmount || formValues.donationAmount,
        donationRenews: formValues.donationType === 'monthly',
        clientToken: tokenRef.current as string,
        existingUser: false,
      });
    }

    history.push(donateLink);
  };

  if (isPending) {
    return <Loader disableBackground />;
  }

  const donationAmounts =
    donationType === 'monthly'
      ? MONTHLY_DONATION_AMOUNTS
      : ONE_DONATION_AMOUNTS;

  return (
    <Stack component="form" noValidate sx={{ flex: 1 }} spacing={4}>
      <Stack spacing={4} sx={{ flex: 1 }}>
        <Stack spacing={2}>
          <Typography sx={{ lineHeight: '22px' }}>
            {t('donate.description')}
          </Typography>

          <Controller
            control={control}
            name="donationType"
            render={({ field }) => {
              return (
                <ToggleButtonGroup
                  fullWidth
                  exclusive
                  value={field.value}
                  onChange={(_, newValue: string | null) =>
                    handleDonationTypeChange(newValue)
                  }
                >
                  <ToggleButton color="primary" value="one-time">
                    {t('donate.donationType.oneTime')}
                  </ToggleButton>
                  <ToggleButton color="primary" value="monthly">
                    {t('donate.donationType.monthly')}
                  </ToggleButton>
                </ToggleButtonGroup>
              );
            }}
          />

          <Controller
            control={control}
            name="donationAmount"
            render={({ field }) => {
              return (
                <ToggleButtonGroup
                  fullWidth
                  exclusive
                  variant="watchduty-gap"
                  value={field.value}
                  onChange={(_, newValue: string | null): void =>
                    handleDonationAmountChange(newValue)
                  }
                >
                  {donationAmounts.map((amount) => (
                    <ToggleButton
                      color="primary"
                      key={`donate-${amount}`}
                      value={amount}
                      size="large"
                    >
                      <b>{formatToUSDollars(amount)}</b>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              );
            }}
          />

          {donationType === 'one-time' && (
            <Controller
              name="customDonationAmount"
              control={control}
              render={({ field, fieldState }) => {
                const { ref, value, onChange, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="custom-donation-amount"
                    label={t('donate.inputs.customAmount.label')}
                    fullWidth
                    type="number"
                    value={value || ''}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    onChange={(event) =>
                      handleCustomDonationAmountChange(event.target.value)
                    }
                    {...muiFieldProps}
                    slotProps={{
                      input: {
                        startAdornment: customDonationAmount ? (
                          <>$</>
                        ) : undefined,
                      },
                      htmlInput: { pattern: '\\d*' },
                    }}
                  />
                );
              }}
            />
          )}
        </Stack>

        {!hasActiveMembership && <MembershipSuggestion />}
      </Stack>

      <Stack spacing={2}>
        <CheckoutPriceSummary
          title={
            donationType === 'one-time'
              ? t('donate.oneTimeDonation')
              : t('donate.monthlyDonation')
          }
          price={formatToUSDollars(customDonationAmount || donationAmount, 2)}
          description={t('donate.footer')}
          priceTestId="total-donation-amount"
        />

        <Button
          fullWidth
          size="large"
          onClick={handleNext}
          disabled={verificationError}
        >
          {t('common.next')}
        </Button>
      </Stack>
    </Stack>
  );
};
